import React, { useState } from 'react';
import { Check, Circle, Shuffle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const MILESTONE_LABELS = {
  '7_day_profile': '7-Day Profile',
  '14_day_bmi': '14-Day BMI',
  '30_day_bca': '30-Day BCA',
};

const REASSIGN_REASONS = [
  { value: 'trainer_changed', label: 'Trainer Changed' },
  { value: 'performance_issue', label: 'Performance Issue' },
  { value: 'schedule_conflict', label: 'Schedule Conflict' },
  { value: 'other', label: 'Other' },
];

/**
 * MilestoneIndicator - renders a single milestone row with status indicator and optional Done button.
 */
function MilestoneIndicator({ milestone, memberName, onComplete }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { type, status, overdue_days, due_date } = milestone;
  const label = MILESTONE_LABELS[type] || type;

  if (status === 'not_applicable') return null;

  let indicator = null;
  let statusText = null;
  let showDoneButton = false;

  switch (status) {
    case 'due_today':
      indicator = <Circle className="w-3.5 h-3.5 fill-yellow-500 text-yellow-500 flex-shrink-0" />;
      statusText = (
        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
          Due Today
        </span>
      );
      showDoneButton = true;
      break;
    case 'overdue':
      indicator = <Circle className="w-3.5 h-3.5 fill-red-500 text-red-500 flex-shrink-0" />;
      statusText = (
        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
          Overdue ({overdue_days}d)
        </span>
      );
      showDoneButton = true;
      break;
    case 'completed':
      indicator = <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />;
      statusText = (
        <span className="text-xs text-green-600">Completed</span>
      );
      break;
    case 'upcoming':
      indicator = <Circle className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />;
      const daysUntil = due_date
        ? Math.max(0, Math.ceil((new Date(due_date) - new Date()) / (1000 * 60 * 60 * 24)))
        : '?';
      statusText = (
        <span className="text-xs text-gray-400">Upcoming ({daysUntil}d)</span>
      );
      break;
    default:
      return null;
  }

  return (
    <>
      <div className="flex items-center justify-between gap-2 py-1.5">
        <div className="flex items-center gap-2 min-w-0">
          {indicator}
          <span className="text-sm text-slate-700">{label}</span>
          {statusText}
        </div>
        {showDoneButton && (
          <button
            onClick={() => setConfirmOpen(true)}
            className="bg-green-500 hover:bg-green-600 text-white text-xs px-3 py-1 rounded-full min-h-[28px] active:scale-95 transition-all flex-shrink-0"
          >
            Done
          </button>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="max-w-sm mx-4">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Complete?</AlertDialogTitle>
            <AlertDialogDescription>
              Mark {memberName}'s {label} as complete?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onComplete(type);
                setConfirmOpen(false);
              }}
              className="bg-green-500 hover:bg-green-600"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * ComplianceMemberCard - Shows a member with their 3 milestone statuses,
 * Done buttons for due/overdue milestones, and trainer reassignment.
 *
 * Props:
 * - member: { member_id, name, join_date, milestones: [...], trainer_name? }
 * - trainers: array of { trainer_id, name } for reassignment dropdown
 * - onCompleteMilestone(memberId, milestoneType): called when milestone marked complete
 * - onReassign(memberId, trainerId, reason): called on trainer reassignment
 */
export default function ComplianceMemberCard({ member, trainers = [], onCompleteMilestone, onReassign }) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [newTrainerId, setNewTrainerId] = useState('');
  const [reason, setReason] = useState('');
  const [reassigning, setReassigning] = useState(false);

  const handleReassign = async () => {
    if (!newTrainerId || !reason) return;
    setReassigning(true);
    try {
      await onReassign(member.member_id, newTrainerId, reason);
      setSheetOpen(false);
      setNewTrainerId('');
      setReason('');
    } finally {
      setReassigning(false);
    }
  };

  const joinDateFormatted = member.join_date
    ? new Date(member.join_date).toLocaleDateString('en-IN', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'N/A';

  return (
    <>
      <div className="rounded-lg border p-4 bg-white shadow-sm">
        {/* Header: Name + Reassign */}
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-slate-800 truncate mr-2">{member.name}</h4>
          <button
            onClick={() => setSheetOpen(true)}
            className="text-slate-400 hover:text-slate-600 p-1.5 rounded-md hover:bg-slate-100 active:scale-95 transition-all min-h-[40px] min-w-[40px] flex items-center justify-center"
            aria-label="Reassign trainer"
          >
            <Shuffle className="w-4 h-4" />
          </button>
        </div>

        {/* Milestone rows */}
        <div className="space-y-0.5">
          {(member.milestones || []).map((milestone) => (
            <MilestoneIndicator
              key={milestone.type}
              milestone={milestone}
              memberName={member.name}
              onComplete={(milestoneType) => onCompleteMilestone(member.member_id, milestoneType)}
            />
          ))}
        </div>

        {/* Footer: Trainer + Join date */}
        <div className="mt-3 pt-2 border-t flex items-center justify-between text-xs text-slate-500">
          {member.trainer_name && (
            <span>Trainer: {member.trainer_name}</span>
          )}
          <span>Joined: {joinDateFormatted}</span>
        </div>
      </div>

      {/* Reassign Sheet (Drawer) */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-xl max-h-[70vh]">
          <SheetHeader>
            <SheetTitle>Reassign {member.name}</SheetTitle>
            <SheetDescription>
              Change the assigned trainer for this member
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-4">
            {member.trainer_name && (
              <div className="text-sm text-slate-600">
                Current Trainer: <span className="font-medium">{member.trainer_name}</span>
              </div>
            )}

            <div>
              <label className="text-sm font-medium mb-1.5 block">New Trainer</label>
              <Select value={newTrainerId} onValueChange={setNewTrainerId}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Select trainer" />
                </SelectTrigger>
                <SelectContent>
                  {trainers.map((t) => (
                    <SelectItem key={t.trainer_id} value={t.trainer_id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Reason</label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {REASSIGN_REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <button
              onClick={handleReassign}
              disabled={!newTrainerId || !reason || reassigning}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2.5 rounded-md font-medium disabled:opacity-50 min-h-[44px] transition-colors"
            >
              {reassigning ? 'Reassigning...' : 'Confirm Reassignment'}
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
